import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import apiService from "../services/api";
import { Button } from "../arc/components/button/button";
import { Input } from "../arc/components/input/input";

// Inject DM Sans, same font as Login / CustomerDashboard
const injectFont = () => {
  if (document.getElementById("dm-sans-font")) return;
  const link = document.createElement("link");
  link.id   = "dm-sans-font";
  link.rel  = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap";
  document.head.appendChild(link);
};

const DM_SANS = "'DM Sans', system-ui, sans-serif";

const ForgotPassword = () => {
  useEffect(() => { injectFont(); }, []);

  const [email, setEmail]     = useState("");
  const [status, setStatus]   = useState("idle"); // "idle" | "loading" | "sent" | "error"
  const [error, setError]     = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setStatus("loading");

    try {
      await apiService.forgotPassword(email);
      setStatus("sent");
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong. Please try again.");
      setStatus("error");
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: "#f1f2f4",
        fontFamily: DM_SANS,
        WebkitFontSmoothing: "antialiased",
      }}
    >
      {/* Ambient gradients, identical to Login */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 60%, rgba(12,44,84,0.08) 0%, transparent 45%), radial-gradient(circle at 85% 15%, rgba(92,113,143,0.1) 0%, transparent 45%), radial-gradient(circle at 50% 95%, rgba(131,148,171,0.07) 0%, transparent 40%)",
        }}
      />

      <div
        className="relative w-full max-w-md p-8 rounded-2xl auth-arc"
        style={{
          background: "linear-gradient(145deg, #0c2c54 0%, #0e3363 50%, #0a2d58 100%)",
          border: "1px solid rgba(131,148,171,0.2)",
          boxShadow:
            "0 24px 64px rgba(12,44,84,0.22), 0 4px 16px rgba(12,44,84,0.12), inset 0 1px 0 rgba(131,148,171,0.12)",
        }}
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div
              style={{
                width: "72px",
                height: "72px",
                borderRadius: "14px",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.18)",
                padding: "6px",
              }}
            >
              <img
                src="/assets/logo.png"
                alt="Axo Signal logo"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </div>
          </div>

          {status !== "sent" && (
            <>
              <p
                style={{
                  fontFamily: DM_SANS,
                  color: "#ffffff",
                  fontSize: "16px",
                  fontWeight: "400",
                  marginTop: "0.5rem",
                  letterSpacing: "0.01em",
                }}
              >
                Reset your password
              </p>
              <p
                style={{
                  fontFamily: DM_SANS,
                  color: "rgba(255,255,255,0.55)",
                  fontSize: "13px",
                  marginTop: "0.35rem",
                  lineHeight: "1.5",
                }}
              >
                Enter your email and we'll send you a reset link.
              </p>
            </>
          )}
        </div>

        {/* ── SUCCESS STATE ── */}
        {status === "sent" ? (
          <div
            style={{
              textAlign: "center",
              padding: "8px 0 16px",
            }}
          >
            {/* Animated checkmark */}
            <div
              style={{
                width: "64px",
                height: "64px",
                borderRadius: "50%",
                background: "rgba(34,197,94,0.15)",
                border: "1px solid rgba(34,197,94,0.35)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
              }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 13l4 4L19 7"
                  stroke="#4ade80"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <p
              style={{
                fontFamily: DM_SANS,
                color: "#ffffff",
                fontSize: "16px",
                fontWeight: "600",
                marginBottom: "8px",
              }}
            >
              Check your inbox
            </p>
            <p
              style={{
                fontFamily: DM_SANS,
                color: "rgba(255,255,255,0.55)",
                fontSize: "13px",
                lineHeight: "1.6",
                marginBottom: "28px",
              }}
            >
              We've sent a password reset link to{" "}
              <span style={{ color: "rgba(255,255,255,0.85)", fontWeight: "500" }}>
                {email}
              </span>
              . It may take a minute to arrive.
            </p>

            {/* Resend option */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => { setStatus("idle"); setError(""); }}
              style={{ fontFamily: DM_SANS, color: "rgba(255,255,255,0.7)" }}
            >
              Didn't receive it? Send again
            </Button>
          </div>
        ) : (
          <>
            {/* ── ERROR BANNER ── */}
            {error && (
              <div
                className="mb-5 p-3 rounded-lg text-sm"
                style={{
                  background: "rgba(239,68,68,0.15)",
                  border: "1px solid rgba(239,68,68,0.35)",
                  color: "#fca5a5",
                  fontFamily: DM_SANS,
                }}
              >
                {error}
              </div>
            )}

            {/* ── FORM ── */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <Input
                label="Email Address"
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@company.com"
                disabled={status === "loading"}
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={status === "loading"}
                style={{ width: "100%", fontFamily: DM_SANS, fontWeight: 600 }}
              >
                {status === "loading" ? "Sending reset link..." : "Send Reset Link"}
              </Button>
            </form>
          </>
        )}

        {/* ── FOOTER LINKS ── */}
        <div
          className="mt-7 pt-6 text-center"
          style={{ borderTop: "1px solid rgba(92,113,143,0.3)" }}
        >
          <p style={{ fontFamily: DM_SANS, fontSize: "14px", color: "#ffffff" }}>
            <Link
              to="/login"
              className="font-semibold transition-colors"
              style={{ color: "#ffffff" }}
              onMouseEnter={(e) => (e.target.style.color = "rgba(255,255,255,0.7)")}
              onMouseLeave={(e) => (e.target.style.color = "#ffffff")}
            >
              ← Back to Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;